export type VideoMode = "watch" | "quiz-only" | "text";

/** Where a stored transcript came from. */
export type TranscriptSource = "youtube" | "browser" | "paste" | "text";

/** True for a quiz built from pasted text rather than a YouTube video. */
export function isTextMode(mode: VideoMode): boolean {
  return mode === "text";
}

/** Header a client sends to assert it paid (mirrors the count-edit paywall). */
export const PAID_ENTITLEMENT_HEADER = "x-vidquiz-payment";
export const PAID_ENTITLEMENT_VALUE = "paid";

/**
 * True when the request carries a paid entitlement. Used for benefits that are
 * generous on the free tier and larger when paid (e.g. the text-input word cap).
 */
export function isPaidEntitled(request: Request): boolean {
  return request.headers.get(PAID_ENTITLEMENT_HEADER) === PAID_ENTITLEMENT_VALUE;
}

/** A quiz level. 1 = Recap, 2 = Understand, 3 = Beyond. */
export type QuizLevel = 1 | 2 | 3;

export interface LevelDefinition {
  level: QuizLevel;
  name: string;
  tagline: string;
  /** Default question count; overridable via env per level. */
  questionCount: number;
  /** How many questions must be out-of-scope (answerable without the video). */
  outOfScopeCount: number;
}

/** Static shape of the level ladder (counts can be overridden via env). */
export const QUIZ_LEVEL_DEFS: readonly LevelDefinition[] = [
  { level: 1, name: "Recap", tagline: "Straight from the video", questionCount: 5, outOfScopeCount: 0 },
  { level: 2, name: "Understand", tagline: "Apply what you learned", questionCount: 8, outOfScopeCount: 0 },
  { level: 3, name: "Beyond", tagline: "Go further than the video", questionCount: 6, outOfScopeCount: 3 },
];

export function isQuizLevel(value: unknown): value is QuizLevel {
  return value === 1 || value === 2 || value === 3;
}

/** Requested number of questions to serve for each level (5..max capped by pool). */
export type QuizCounts = Record<QuizLevel, number>;

/** Per-video quiz serving options for each level. */
export interface QuizOptions {
  questionCounts: QuizCounts;
}

export const VIDEO_STATUS = {
  QUEUED: "queued",
  FETCHING_TRANSCRIPT: "fetching_transcript",
  FORMATTING_TRANSCRIPT: "formatting_transcript",
  GENERATING_QUESTIONS: "generating_questions",
  READY: "ready",
  FAILED: "failed",
} as const;

export type VideoStatus = (typeof VIDEO_STATUS)[keyof typeof VIDEO_STATUS];

/** Ordered pipeline stages used for progress reporting. */
export const PIPELINE_STAGES: readonly VideoStatus[] = [
  VIDEO_STATUS.QUEUED,
  VIDEO_STATUS.FETCHING_TRANSCRIPT,
  VIDEO_STATUS.FORMATTING_TRANSCRIPT,
  VIDEO_STATUS.GENERATING_QUESTIONS,
  VIDEO_STATUS.READY,
];

/** Statuses a row sits in while a consumer owns the job. */
export const IN_FLIGHT_STATUSES: readonly VideoStatus[] = [
  VIDEO_STATUS.FETCHING_TRANSCRIPT,
  VIDEO_STATUS.FORMATTING_TRANSCRIPT,
  VIDEO_STATUS.GENERATING_QUESTIONS,
];

/**
 * How long a row may go untouched before it counts as abandoned. Set above the
 * queue consumer's 15-minute wall-clock limit plus the model-call slack, so a
 * slow-but-alive run is never stolen from under a working consumer.
 */
export const STALE_JOB_SECONDS = 900;

/** Abandoned jobs requeued by the sweeper before they are failed outright. */
export const SWEEP_MAX_ATTEMPTS = 3;

/** Upper bound on rows a single sweep pass will touch. */
export const SWEEP_BATCH_LIMIT = 25;

export interface VideoSummary {
  videoId: string;
  status: VideoStatus;
  stage: VideoStatus | null;
  mode: VideoMode;
  error: string | null;
  /** Machine-readable reason for the last failure (e.g. TRANSCRIPT_NEEDS_BROWSER). */
  errorCode?: string | null;
  /** True when the source text was longer than the tier budget and got trimmed before generation. */
  truncated?: boolean;
  title: string | null;
  channel: string | null;
  thumbnail: string | null;
  durationSeconds: number | null;
  progress: number;
  createdAt: string;
  updatedAt: string;
  quiz: QuizSummary | null;
  /** Per-level availability + gating for the requesting client. */
  levels: VideoLevelStatus[];
  /**
   * Per-stage pipeline durations, present once the first stage has been
   * recorded. Optional/additive so older databases and clients are unaffected.
   */
  timings?: StageTiming[];
}

/** One recorded pipeline stage (transcript, format, generation, total). */
export interface StageTiming {
  stage: string;
  durationMs: number;
  model: string | null;
  status: "ok" | "failed";
  detail: Record<string, unknown> | null;
  createdAt: string;
}

export interface QuizSummary {
  id: string;
  level: QuizLevel;
  questionCount: number;
  model: string;
  createdAt: string;
}

/** A level whose quiz pool exists, as shown on a History/Browse card. */
export interface HistoryLevelInfo {
  level: QuizLevel;
  name: string;
  tagline: string;
  /** Served (requested) question count for the level, not the pool size. */
  questionCount: number;
}

/**
 * A single ready quiz in the public library. Deliberately lightweight — no
 * per-client attempt state — because a page of results is built with two
 * queries rather than the per-video `summarizeVideo` fan-out.
 */
export interface VideoHistoryItem {
  videoId: string;
  mode: VideoMode;
  /** Text-input quizzes have no player and are quiz-only. */
  isText: boolean;
  title: string | null;
  channel: string | null;
  thumbnail: string | null;
  durationSeconds: number | null;
  createdAt: string;
  /** Levels that are actually available, in ascending order. */
  levels: HistoryLevelInfo[];
  /** Sum of the available levels' served question counts. */
  totalQuestions: number;
}

/** One page of the History/Browse feed. */
export interface VideoHistoryPage {
  items: VideoHistoryItem[];
  /** Opaque cursor for the next page, or null when there are no more. */
  nextCursor: string | null;
  hasMore: boolean;
  /** Total ready videos matching the current search (ignores pagination). */
  total: number;
}

export interface HistoryCursor {
  createdAt: number;
  videoId: string;
}

/** Encode the keyset position of a feed row as an opaque cursor string. */
export function encodeHistoryCursor(createdAt: number, videoId: string): string {
  return `${createdAt}:${videoId}`;
}

/** Parse a cursor produced by `encodeHistoryCursor`; returns null when invalid. */
export function decodeHistoryCursor(cursor: string | null | undefined): HistoryCursor | null {
  if (!cursor) return null;
  const sep = cursor.indexOf(":");
  if (sep <= 0) return null;
  const createdAt = Number.parseInt(cursor.slice(0, sep), 10);
  const videoId = cursor.slice(sep + 1);
  if (!Number.isFinite(createdAt) || videoId.length === 0) return null;
  return { createdAt, videoId };
}

/**
 * Per-level availability + progress for the requesting client. `available`
 * means the quiz for the level has been generated; `unlocked` additionally
 * requires the previous level to have been passed (level 1 is always unlocked
 * once available).
 */
export interface VideoLevelStatus {
  level: QuizLevel;
  name: string;
  tagline: string;
  questionCount: number | null;
  available: boolean;
  unlocked: boolean;
  bestPercent: number | null;
  passed: boolean;
}

/** Whether a question is answerable from the transcript or goes beyond it. */
export type QuizQuestionScope = "video" | "beyond";

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  /** 0-based index of the correct option in `options`. */
  correctIndex: number;
  /** Short explanation shown after answering. */
  explanation: string;
  /** Only set on "Beyond" (level 3) questions. */
  scope?: QuizQuestionScope;
}

export interface Quiz {
  id: string;
  videoId: string;
  level: QuizLevel;
  model: string;
  createdAt: string;
  questions: QuizQuestion[];
}

/** Quiz shape sent to clients BEFORE scoring — answer key is withheld. */
export interface PublicQuizQuestion {
  id: string;
  question: string;
  options: string[];
  scope?: QuizQuestionScope;
}

export interface PublicQuiz {
  id: string;
  videoId: string;
  level: QuizLevel;
  model: string;
  createdAt: string;
  /** Questions served for this attempt (a deterministic slice of the pool). */
  questions: PublicQuizQuestion[];
  /** How many questions were actually served (≤ pool size). */
  requestCount?: number;
}

export function toPublicQuiz(quiz: Quiz): PublicQuiz {
  return {
    id: quiz.id,
    videoId: quiz.videoId,
    level: quiz.level,
    model: quiz.model,
    createdAt: quiz.createdAt,
    questions: quiz.questions.map((q) => ({
      id: q.id,
      question: q.question,
      options: q.options,
      ...(q.scope ? { scope: q.scope } : {}),
    })),
  };
}

export interface QuizAttemptInput {
  /** One option index per question, aligned by position. */
  answers: number[];
  level?: QuizLevel;
}

export interface AttemptQuestionResult {
  questionId: string;
  selected: number;
  correctIndex: number;
  isCorrect: boolean;
  explanation: string;
}

export interface QuizAttemptResult {
  attemptId: string;
  level: QuizLevel;
  score: number;
  total: number;
  percent: number;
  passed: boolean;
  results: AttemptQuestionResult[];
}

/** Transcript segment with timing, before any formatting. */
export interface TranscriptSegment {
  start: number;
  duration: number;
  text: string;
}

export interface VideoMetadata {
  title: string | null;
  channel: string | null;
  thumbnail: string | null;
  durationSeconds: number | null;
}

export interface TranscriptResult {
  language: string | null;
  isGenerated: boolean;
  segments: TranscriptSegment[];
  metadata: VideoMetadata;
}

export interface FormattedTranscript {
  text: string;
  language: string | null;
  segmentCount: number;
  durationSeconds: number | null;
  /** True when the transcript was truncated for token/cost safety. */
  truncated: boolean;
}

/**
 * A transcript supplied by the user's browser (bookmarklet) or pasted in, or a
 * block of source text submitted for a text-input quiz. Stored so the pipeline
 * can skip the YouTube fetch entirely.
 */
export interface StoredTranscript {
  source: TranscriptSource;
  language: string | null;
  /** Cleaned, un-windowed source text. */
  text: string;
  segmentCount: number;
  /** Tier model-input budget in chars for text-mode sources (0007); else null. */
  maxChars?: number | null;
}

export interface PublicConfig {
  model: string;
  questionCount: number;
  maxTranscriptChars: number;
  maxQuestionsPerAttempt: number;
  /** Bounds users may pick per level. */
  minQuestionsPerLevel: number;
  maxQuestionsPerLevel: number;
  /** Full pool size generated per level (sliced to the requested count). */
  poolSize: number;
  /** True when editing counts on existing videos requires a paid entitlement. */
  editRequiresPayment: boolean;
  /** Text-input mode bounds. Free/paid are model-input budgets: longer text is
   * accepted but trimmed; `maxTextWordsAccepted` is the hard accept ceiling. */
  minTextWords: number;
  maxTextWordsFree: number;
  maxTextWordsPaid: number;
  maxTextWordsAccepted: number;
  /** Env defaults. questionCount mirrors levels[1].questionCount. */
  levels: LevelDefinition[];
}

export interface HealthReport {
  status: "ok";
  service: string;
  version: string;
  time: string;
  uptime: number;
  checks: {
    db: string;
    openrouter: string;
  };
}